import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTotalCountAction,
  fetchTotalRevenueAction,
  fetchTopUsersBySpendingAction,
  fetchTopUsersByPackagesAction,
  fetchTopDriversAction,
  fetchStatusDistributionAction,
  fetchDataByDateAction,
  fetchRecentTransactionsAction
} from "state/actions/analyticsActions";

import {
  Flex,
  Grid,
  SimpleGrid,
  useColorModeValue,
  Icon,
} from "@chakra-ui/react";

import {
  Users,
  UserCheck,
  UserX,
  Car,
  Clock,
  CheckCircle2,
  XCircle,
  DollarSign,
  Package,
  TrendingUp,
  Truck,
  AlertTriangle
} from "lucide-react";

import {
  FaArrowDown,
  FaArrowUp,
} from "react-icons/fa";

import ActiveUsers from "../Dashboard/components/ActiveUsers";
import MiniStatistics from "../Dashboard/components/MiniStatistics";
import SalesOverview from "../Dashboard/components/SalesOverview";
import LineChart from "components/Charts/LineChart";
import { formatDate } from "utils/formatDate";

export default function Statistics() {
  const { t } = useTranslation();
  const iconBoxInside = useColorModeValue("white", "white");
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);

  const usersData = useSelector(state => state.analytics.counts.users || {});
  const vehiclesData = useSelector(state => state.analytics.counts.vehicles || {});
  const driversData = useSelector(state => state.analytics.counts.drivers || {});
  const packagesData = useSelector(state => state.analytics.counts.packages || {});

  const totalRevenue = useSelector(state => state.analytics.totalRevenue);
  const todaysRevenue = useSelector(state => state.analytics.todaysRevenue);

  const deliveryStatus = useSelector(state => state.analytics.statusDistribution.deliveries);
  const dataByDate = useSelector(state => state.analytics.dataByDate);
  const recentTransactions = useSelector(state => state.analytics.recentTransactions);

  // USERS
  const usersCount = usersData?.total || 0;
  const activeUsers = usersData?.active || 0;
  const inactiveUsers = usersData?.inactive || 0;

  // VEHICLES
  const approvedVehicles = vehiclesData?.approved || 0;
  const rejectedVehicles = vehiclesData?.rejected || 0;

  // DRIVERS
  const approvedDrivers = driversData?.approved || 0;
  const rejectedDrivers = driversData?.rejected || 0;

  // PACKAGES
  const completedPackages = packagesData?.completed || 0;
  const pendingPackages = packagesData?.pending || 0;

  // DELIVERY STATUS
  const inProgressCount = deliveryStatus?.["in_progress"] || 0;
  const failedCount = deliveryStatus?.["failed"] || 0;

  const getLatestGrowth = (tableName) =>
    dataByDate?.[tableName]?.length
      ? dataByDate[tableName][0]?.growth
      : 0;

  const usersGrowth = getLatestGrowth("users");
  const vehiclesGrowth = getLatestGrowth("vehicles");
  const transactionsGrowth = getLatestGrowth("transactions");

  useEffect(() => {
    setTimeout(() => setIsLoading(false), 3000);

    dispatch(fetchTotalCountAction("users"));
    dispatch(fetchTotalCountAction("users", "Active"));
    dispatch(fetchTotalCountAction("users", "Inactive"));

    dispatch(fetchTotalCountAction("vehicles", "Approved"));
    dispatch(fetchTotalCountAction("vehicles", "Rejected"));

    dispatch(fetchTotalCountAction("drivers", "Approved"));
    dispatch(fetchTotalCountAction("drivers", "Rejected"));

    dispatch(fetchTotalCountAction("packages", "Collected"));
    dispatch(fetchTotalCountAction("packages", "Pending"));

    dispatch(fetchTotalCountAction("transactions"));
    dispatch(fetchTotalRevenueAction("transactions"));

    dispatch(fetchTopUsersBySpendingAction("transactions"));
    dispatch(fetchTopUsersByPackagesAction("packages"));
    dispatch(fetchStatusDistributionAction("deliveries"));

    dispatch(fetchDataByDateAction("users"));
    dispatch(fetchDataByDateAction("vehicles"));
    dispatch(fetchDataByDateAction("transactions"));
    dispatch(fetchDataByDateAction("packages"));

    dispatch(fetchTopDriversAction(100));
    dispatch(fetchRecentTransactionsAction(100));

  }, [dispatch]);

  const getDirectionIcon = (current, previous) => {
    if (previous == null) return null;

    return current > previous
      ? <Icon as={FaArrowUp} color="green.400" boxSize={4} />
      : <Icon as={FaArrowDown} color="red.400" boxSize={4} />;
  };

  let previousAmount = null;
  const newest = recentTransactions?.slice(0, 3).map((t, i) => {
    const icon = i === 0 ? null : getDirectionIcon(t.amount, previousAmount);
    previousAmount = t.amount;

    return {
      name: t.username || 'Unknown',
      logo: icon,
      date: formatDate(t.created_at),
      price: `P${(t.amount || 0).toFixed(2)}`
    };
  });

  return (
    <Flex flexDirection='column'>

      <SimpleGrid columns={{ sm: 1, md: 2, xl: 4 }} spacing='24px' pb="20px">

        {/* Revenue */}
        <MiniStatistics
          title={"Today's Revenue"}
          amount={`P${todaysRevenue || "0"}`}
          percentage={0}
          icon={<DollarSign />}
          isLoading={isLoading}
        />

        {/* Users */}
        <MiniStatistics
          title={'Total Users'}
          amount={usersCount}
          percentage={usersGrowth}
          icon={<Users />}
          isLoading={isLoading}
        />

        <MiniStatistics
          title={'Active Users'}
          amount={activeUsers}
          percentage={0}
          icon={<UserCheck />}
          isLoading={isLoading}
        />

        <MiniStatistics
          title={'Inactive Users'}
          amount={inactiveUsers}
          percentage={0}
          icon={<UserX />}
          isLoading={isLoading}
        />

        {/* Vehicles */}
        <MiniStatistics
          title={'Approved Vehicles'}
          amount={approvedVehicles}
          percentage={vehiclesGrowth}
          icon={<Car />}
          isLoading={isLoading}
        />

        <MiniStatistics
          title={'Rejected Vehicles'}
          amount={rejectedVehicles}
          percentage={0}
          icon={<Clock />}
          isLoading={isLoading}
        />

        {/* Drivers */}
        <MiniStatistics
          title={'Approved Drivers'}
          amount={approvedDrivers}
          percentage={0}
          icon={<Truck />}
          isLoading={isLoading}
        />

        <MiniStatistics
          title={'Rejected Drivers'}
          amount={rejectedDrivers}
          percentage={0}
          icon={<Clock />}
          isLoading={isLoading}
        />

        {/* Deliveries */}
        <MiniStatistics
          title={'Completed Deliveries'}
          amount={completedPackages}
          percentage={0}
          icon={<CheckCircle2 />}
          isLoading={isLoading}
        />

        <MiniStatistics
          title={'Failed Deliveries'}
          amount={failedCount}
          percentage={0}
          icon={<XCircle />}
          isLoading={isLoading}
        />

        <MiniStatistics
          title={'Pending Packages'}
          amount={pendingPackages}
          percentage={0}
          icon={<Package />}
          isLoading={isLoading}
        />

        {/* Total Sales */}
        <MiniStatistics
          title={t('total_sales')}
          amount={`P${totalRevenue || "0"}`}
          percentage={transactionsGrowth}
          icon={<TrendingUp />}
          isLoading={isLoading}
        />

      </SimpleGrid>

      <Grid templateColumns={{ sm: "1fr", lg: "1.3fr 1.7fr" }} gap='24px'>

        <ActiveUsers
          title={"Users Trend"}
          percentage={usersGrowth}
          chart={<LineChart data={dataByDate?.users || []} />}
          isLoading={isLoading}
        />

        <SalesOverview
          title={t("sales_overview")}
          percentage={transactionsGrowth}
          chart={<LineChart data={dataByDate?.transactions || []} />}
          isLoading={isLoading}
        />

      </Grid>

    </Flex>
  );
}
